import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useUpdateDealSetupNote } from '@/lib/hooks/useUpdateDealSetupNote';
import {
  ensureDealSetupPositionPhrase,
  noteContainsAnyPositionColumnPhrase,
  stripDealSetupPositionPhrase,
} from '@/lib/utils/position-column-phrase';
import Button from '@appchoose/button';

import { SetupNoteEditor } from './setup-note-modal';

export function ValidateOfferConfirmationContent({
  hasBrandValidated,
}: {
  hasBrandValidated: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-center text-lg font-semibold">
          {t('components.validate_offer_button.modal_title')}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="flex flex-col space-y-1 text-sm">
          {hasBrandValidated ? (
            <p className="text-slate-800">
              •{' '}
              {t(
                'components.validate_offer_button.close_negotiation_brand_already_validated'
              )}
            </p>
          ) : (
            <p className="text-amber-700">
              ⚠️{' '}
              {t(
                'components.validate_offer_button.close_negotiation_warning_brand_not_validated'
              )}
            </p>
          )}
        </div>

        <div className="flex flex-col space-y-1 text-sm">
          <p className="text-slate-800">
            • {t('components.validate_offer_button.warning_finalized')}
          </p>
          <p className="text-slate-800">
            • {t('components.validate_offer_button.reassurance_consult')}
          </p>
        </div>
      </div>
    </>
  );
}

export interface ValidateOfferConfirmationModalProps {
  dealId: string;
  currentNote: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  /** For mode === 'close_only': when false, show extra warning that brand has not validated. */
  hasBrandValidated?: boolean;
}

function ValidateOfferConfirmationModal({
  dealId,
  currentNote,
  open,
  onOpenChange,
  onConfirm,
  hasBrandValidated = true,
}: ValidateOfferConfirmationModalProps) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [positionOrderChoice, setPositionOrderChoice] = useState<
    'yes' | 'no' | undefined
  >(undefined);
  const prevOpenRef = useRef(false);
  const { updateSetupNote, loading } = useUpdateDealSetupNote(dealId);
  const { toast } = useToast();

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (!justOpened) return;
    const n = currentNote || '';
    setNote(n);
    setPositionOrderChoice(
      noteContainsAnyPositionColumnPhrase(n) ? 'yes' : undefined
    );
  }, [open, currentNote]);

  const baseline = currentNote || '';

  const computeFinalNote = (
    rawNote: string,
    choice: 'yes' | 'no' | undefined
  ): string => {
    if (choice === 'yes') return ensureDealSetupPositionPhrase(rawNote, true);
    if (choice === 'no') return stripDealSetupPositionPhrase(rawNote);
    return rawNote;
  };

  const persistIfNeeded = async (finalNote: string): Promise<boolean> => {
    if (finalNote === baseline) return true;
    try {
      const result = await updateSetupNote(finalNote);
      if (!result.success) {
        toast({
          title: t('general.error'),
          description: t('components.setup_note_modal.note_save_error'),
          variant: 'destructive',
        });
        return false;
      }
      return true;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la note:', error);
      toast({
        title: t('general.error_network'),
        description: t('general.error_network_message'),
        variant: 'destructive',
      });
      return false;
    }
  };

  const dismissWithPersist = async () => {
    if (loading) return;
    const finalNote = computeFinalNote(note, positionOrderChoice);
    const ok = await persistIfNeeded(finalNote);
    if (!ok) return;
    setNote(finalNote);
    onOpenChange(false);
  };

  const handleConfirm = async () => {
    if (positionOrderChoice === undefined || loading) return;
    const finalNote = computeFinalNote(note, positionOrderChoice);
    const ok = await persistIfNeeded(finalNote);
    if (!ok) return;
    setNote(finalNote);
    onOpenChange(false);
    onConfirm();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) return;
        void dismissWithPersist();
      }}
    >
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 sm:max-w-[600px]">
        <div className="min-h-0 flex-1 overflow-y-auto">
          <ValidateOfferConfirmationContent
            hasBrandValidated={hasBrandValidated}
          />
          <DialogHeader>
            <DialogDescription asChild>
              <p className="text-slate-800">
                ⚠️ {t('components.setup_note_modal.warning_after_validation')}
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span className="text-sm text-slate-800">
                {t('components.validate_offer_button.position_order_question')}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  appearance={
                    positionOrderChoice === 'yes' ? 'primary' : 'outline'
                  }
                  disabled={loading}
                  onClick={() => {
                    setPositionOrderChoice('yes');
                    setNote((n) => ensureDealSetupPositionPhrase(n, true));
                  }}
                >
                  {t('components.validate_offer_button.position_order_yes')}
                </Button>
                <Button
                  type="button"
                  appearance={
                    positionOrderChoice === 'no' ? 'primary' : 'outline'
                  }
                  disabled={loading}
                  onClick={() => {
                    setPositionOrderChoice('no');
                    setNote((n) => stripDealSetupPositionPhrase(n));
                  }}
                >
                  {t('components.validate_offer_button.position_order_no')}
                </Button>
              </div>
            </div>
            <SetupNoteEditor
              value={note}
              onChange={setNote}
              disabled={loading}
            />
          </div>
        </div>
        <DialogFooter className="shrink-0 border-t pt-4">
          <Button
            type="button"
            appearance="outline"
            onClick={() => void dismissWithPersist()}
            disabled={loading}
          >
            {t('general.cancel')}
          </Button>
          <Button
            type="button"
            appearance="primary"
            onClick={() => void handleConfirm()}
            disabled={loading || positionOrderChoice === undefined}
          >
            {loading ? t('general.saving') : t('general.validate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ValidateOfferConfirmationModal;
